"use client";

// Composant client depuis le 22/09, pour une seule raison : la CHORÉGRAPHIE D'ENTRÉE a besoin de savoir quand le bloc
// est à l'écran et quand l'onglet est caché. Tout le reste du bloc — les deux cartes, la liste, les étiquettes — est
// dans le HTML rendu au serveur et se lit sans JavaScript ; la balise de la <section> n'a pas bougé d'un caractère
// (tests/home-v2-bottom.test.tsx la compare au mot près).

import Image from "next/image";
import { useEffect, useRef } from "react";
import SectionHeading from "@/components/ds/section-heading";
import Reveal from "@/components/motion/reveal";
import { stagger } from "@/components/motion/stagger";
import { brandText, HABIT_MARK, HABIT_MARK_MASK, type Brand } from "./brand";
import "./habit.css";
import "./daily-claude.css";

// Les cinq mêmes intitulés existent dans components/home-v2/firm-brain.tsx (LAYERS), et là-bas ils sont NUMÉROTÉS pour
// une raison vraie : ce sont les couches d'une pile en 3D, « 0 is the head card in front, 5 the last layer at the
// back », rendues dans un <ol> et annoncées couche par couche dans l'aria-label des cartes. Ici il n'y a pas de pile :
// rien n'est devant rien, et le chapô du bloc les énumère lui-même en prose, reliés par « and ». Les chiffres 01 à 05
// étaient donc un fossile de l'autre bloc — le balisage (<ul>) disait « aucun ordre » pendant que les chiffres
// disaient « un ordre ». C'est le <ul> qui avait raison : les numéros partent, et rien de décoratif ne les remplace.
const KNOWLEDGE = ["Emails", "Documents", "Matters", "Meeting recordings", "Custom skills"];

/* LES CINQ TESSONS DE LA MARQUE NORTH, recopiés au caractère près depuis public/assets/v2/top/north-mark-nav.svg
   (SHAPE_1 à SHAPE_5, viewBox 0 0 32 32.2371). Pourquoi ILS SONT ICI et plus dans le fichier : le motif de fond de
   la carte claire doit être un CONTOUR et non un aplat (Jérémy, 22/09 : « applique-le uniquement à un stroke
   outline : ne mets pas de couleur intérieure, mais uniquement un gradient sur les bordures de chaque élément du
   logo »), et un masque CSS — le procédé employé jusqu'ici — ne sait faire que des aplats : il découpe une forme
   dans une couleur, il n'a aucune notion de trait. Le seul moyen de poser fill: none + stroke: url(#…) sur chacun
   des cinq tessons est d'avoir les cinq <path> dans le document.
   LE FICHIER RESTE LA SOURCE et n'est pas modifié : il sert toujours de masque à la petite marque de la ligne
   North OS (.v2-daily__mark) et à la marque de la barre de navigation. C'est une copie, pas un déménagement — et
   c'est une dette : si le fichier bouge, ces cinq chaînes ne bougeront pas avec lui. */
const MARK_SHAPES = [
  "M14.6568 0L9.01412 14.6696L14.1037 13.1866L16.9481 17.8685L18.7365 11.8687L20.6359 11.2035L14.6568 0Z",
  "M32 7.30201L20.314 12.9891L18.4146 20.3764L32 7.30201Z",
  "M12.1679 15.0718L0 18.5939L8.92839 21.4718L12.1284 15.0718H12.1679Z",
  "M13.6003 16.1186L6.01509 30.7226L17.1911 21.1816L13.6003 16.1186Z",
  "M21.6991 19.0505L16.6818 23.6036L27.0325 32.2371L21.7386 19.0076L21.6991 19.0505Z",
];

/* L'identifiant du dégradé est UNIQUE et préfixé par le bloc, et ce n'est pas de la coquetterie : deux <linearGradient>
   qui portent le même id sur une page se marchent dessus, le second gagne pour tout le monde, et le dépôt a déjà payé
   ce bug (voir le commentaire de components/ds/button.tsx). */
const GLYPH_GRADIENT = "v2-daily-glyph-stroke";

/* Le même mécanisme que la constellation de Skills (skills.tsx), et pour les mêmes raisons. Il pose data-motion sur la
   GRILLE (.v2-daily__flow), un élément INTÉRIEUR : « live » quand le bloc est à 30 % à l'écran et l'onglet visible,
   « idle » sinon (daily-claude.css met alors --play: paused, injecté dans chaque animation : tout se fige où c'est, et
   rien ne tourne dans le vide), et ABSENT sous prefers-reduced-motion — la feuille n'a alors aucune animation à jouer,
   ni aucun état de départ, donc le bloc est fini d'emblée.
   L'attribut ne peut pas aller sur la <section> : sa balise est figée au mot près par les tests. */
function useStage() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const flow = ref.current;
    if (!flow) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = false;

    const update = () => {
      if (reduce.matches) delete flow.dataset.motion;
      else flow.dataset.motion = visible && !document.hidden ? "live" : "idle";
    };

    // Sans IntersectionObserver, la chorégraphie part tout de suite plutôt que jamais.
    if (!("IntersectionObserver" in window)) {
      visible = true;
      update();
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = !!entry?.isIntersecting;
        update();
      },
      { threshold: 0.3 },
    );
    io.observe(flow);
    document.addEventListener("visibilitychange", update);
    reduce.addEventListener("change", update);
    update();

    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", update);
      reduce.removeEventListener("change", update);
    };
  }, []);

  return ref;
}

/* `brand` (brand.ts): "north" unless the /logo-test preview says "habit". The two marks of the light card then take
   the Habit drawing — the big outline behind the list (six shapes in a 70 x 70 square, their tracing lengths in
   habit.css) and the small one beside the product's name (the same mask, on the same ink). */
export default function DailyClaude({ brand = "north" }: { brand?: Brand }) {
  const flow = useStage();
  const habit = brand === "habit";
  const glyph = habit ? { shapes: HABIT_MARK, width: 70, height: 70 } : { shapes: MARK_SHAPES, width: 32, height: 32.2371 };

  return (
    <Reveal as="section" id="daily-claude" className="v2-daily" aria-label="Your everyday AI">
      <div className="v2-daily__inner">
        <SectionHeading
          tag="Your everyday AI"
          tagVariant="blue"
          align="center"
          title={["Every day, Claude.", brandText("Supercharged by North.", brand)]}
          lead={brandText("You work in Claude. North brings your firm's knowledge into it: your emails, documents, matters, meeting recordings, and custom skills. So every conversation starts with your firm's context.", brand)}
          reveal
        />
        {/* Le chapô monte avec la révélation ordinaire du site (rangs 0 à 2 de SectionHeading). Ce qui suit ne monte
            plus ligne à ligne : la paire a sa propre chorégraphie, écrite dans daily-claude.css, et elle RACONTE la
            phrase du bloc — deux blocs en contour se séparent, le savoir arrive à gauche, Claude se charge à droite,
            la connexion se fait au milieu. Elle remplace donc la révélation de ces éléments, comme les balles de
            Skills remplacent celle des pastilles. */}
        <div className="v2-daily__flow" ref={flow}>
          <div className="v2-daily__brain">
            {/* La marque North en très grand, coupée par le coin bas-droit : un MOTIF de fond, pas un logo.
                Rien à lire, rien dans l'arbre d'accessibilité. Voir daily-claude.css.
                Le <span> porte le RECADRAGE (overflow + rayon), le <svg> porte le DESSIN : c'est la même division du
                travail qu'avant, le ::before est simplement devenu un élément réel parce qu'un contour a besoin de
                <path>. preserveAspectRatio="none" reproduit exactement ce que faisait mask-size: 100% 100% — la
                marque s'étire dans son carré (la viewBox fait 32 × 32,2371, donc l'étirement vaut 0,7 %).
                Les couleurs des deux arrêts sont dans la feuille, pas ici : la palette reste aux jetons. */}
            <span className="v2-daily__glyph" aria-hidden="true">
              <svg
                className="v2-daily__glyph-art"
                viewBox={`0 0 ${glyph.width} ${glyph.height}`}
                preserveAspectRatio="none"
                focusable="false"
                data-brand={habit ? brand : undefined}
              >
                <linearGradient id={GLYPH_GRADIENT} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={glyph.width} y2={glyph.height}>
                  <stop className="v2-daily__glyph-from" offset="0" />
                  <stop className="v2-daily__glyph-to" offset="1" />
                </linearGradient>
                {/* vector-effect : la marque est agrandie ~17 fois (32 px de viewBox pour ~555 px à l'écran), donc
                    sans lui un trait d'un pixel arriverait à dix-sept. Le trait reste un trait, à toute taille. */}
                {glyph.shapes.map((d) => (
                  <path key={d} d={d} stroke={`url(#${GLYPH_GRADIENT})`} vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
            </span>
            <div className="v2-daily__brand">
              {/* north-mark-nav.svg est un aplat BLANC : il était rendu par filter: brightness(0), donc en noir pur,
                  une couleur qui n'est pas dans la palette. Repris en masque sur --v2-ink, comme app/brand/brand.css. */}
              <span
                className="v2-daily__mark"
                aria-hidden="true"
                style={habit ? { maskImage: HABIT_MARK_MASK, WebkitMaskImage: HABIT_MARK_MASK } : undefined}
              />
              <span>{brandText("North OS", brand)}</span>
            </div>
            <h3>Your firm’s knowledge.</h3>
            <ul className="v2-daily__knowledge">
              {/* --i : le rang de la ligne dans l'arrivée du savoir (délai = i × --v2-stagger, le pas commun du site). */}
              {KNOWLEDGE.map((item, index) => (
                <li key={item} style={stagger(index)}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="v2-daily__connection" aria-hidden="true">
            <span>Connected to Claude</span>
            {/* Le rail est un élément VIDE depuis le 22/09 au soir. Il portait un <span> : la pastille qui voyageait
                de North OS à Claude. Jérémy l'a retirée après l'avoir vue (« pas la boule qui se déplace, ce n'est
                pas nécessaire »), et ce qui la remplace — trois ondes de couleur qui parcourent le trait — n'a
                besoin d'aucun corps : ce sont des arrêts du dégradé du rail lui-même. Voir daily-claude.css. */}
            <div className="v2-daily__line" />
            <span>Your firm’s context</span>
          </div>
          <div className="v2-daily__claude">
            {/* Le grain de la carte, sur un calque à lui, SOUS tout le contenu : voir daily-claude.css. */}
            <span className="v2-daily__grain" aria-hidden="true" />
            {/* Le reflet de chargement : rien à lire, rien dans l'arbre d'accessibilité, invisible au repos. */}
            <span className="v2-daily__sheen" aria-hidden="true" />
            <div className="v2-daily__brand">
              {/* La marque de Claude, telle qu'elle est : le corail #D97757 de son dégradé. Le filter: brightness(1.8)
                  qui la délavait est retiré — il repeignait la marque d'un partenaire, et il ne servait même pas la
                  lisibilité : le corail nu tient déjà 5,92:1 sur --v2-ink-deep (mesuré, lib/contrast.ts). */}
              <Image src="/assets/v2/top/hero-asterisk-coral.svg" alt="" width={30} height={30} />
              <span>Claude</span>
            </div>
            <h3>The AI you use.<br />The firm you know.</h3>
            <p>Draft, ask, review, and prepare with your firm’s knowledge close at hand.</p>
            <div className="v2-daily__context">Your knowledge. Your history. Your skills.</div>
          </div>
        </div>
        <p className="v2-daily__closing">
          You bring the legal judgment.<br className="v2-daily__mobile-break" />{brandText(" North brings the firm’s memory.", brand)}
        </p>
      </div>
    </Reveal>
  );
}
